import React, { useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Space,
  Typography,
  message,
} from 'antd';
import HierarchicalPanelPicker from './HierarchicalPanelPicker';
import { SourceEpisode, SourcePanel, SourceProcess, SourceScene } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { useApi, useAsyncOperation } from '../../hooks';

const { Text } = Typography;
const { Option } = Select;

interface EditEpisodeModalProps {
  visible: boolean;
  episode: SourceEpisode | null;
  storyId: string;
  onClose: () => void;
  onSuccess: () => void;
  nextOrderIndex: number;
}

const EditEpisodeModal: React.FC<EditEpisodeModalProps> = ({
  visible,
  episode,
  storyId,
  onClose,
  onSuccess,
  nextOrderIndex,
}) => {
  const [form] = Form.useForm();
  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch all processes for the story
  const {
    data: processesData,
    loading: processesLoading,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]),
    { immediate: visible }
  );

  // Fetch all scenes for the story
  const {
    data: scenesData,
    loading: scenesLoading,
  } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourceScenes(storyId), [storyId]),
    { immediate: visible }
  );

  // Fetch all panels for the story
  const {
    data: panelsData,
    loading: panelsLoading,
  } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]),
    { immediate: visible }
  );

  const allProcesses = processesData?.data || [];
  const allScenes = scenesData?.data || [];
  const allPanels = panelsData?.data || [];

  const isLoading = processesLoading || scenesLoading || panelsLoading;

  useEffect(() => {
    if (visible) {
      if (episode) {
        form.setFieldsValue(episode);
      } else {
        form.resetFields();
        form.setFieldsValue({
          name: `Episode ${nextOrderIndex}`,
          orderIndex: nextOrderIndex,
          status: 'DRAFT',
        });
      }
    }
  }, [visible, episode, form, nextOrderIndex]);

  const handleSubmit = async (values: any) => {
    // Validation: End Panel must be after or equal to Start Panel
    const startPanel = allPanels.find(p => p.id === values.startPanelId);
    const endPanel = allPanels.find(p => p.id === values.endPanelId);

    if (startPanel && endPanel) {
      // Find hierarchy for comparison
      const startScene = allScenes.find(s => s.id === startPanel.sceneId);
      const endScene = allScenes.find(s => s.id === endPanel.sceneId);
      
      if (startScene && endScene) {
        const startProcess = allProcesses.find(p => p.id === startScene.processId);
        const endProcess = allProcesses.find(p => p.id === endScene.processId);

        if (startProcess && endProcess) {
          let isValid = true;
          if (startProcess.orderIndex > endProcess.orderIndex) {
            isValid = false;
          } else if (startProcess.orderIndex === endProcess.orderIndex) {
            if (startScene.orderIndex > endScene.orderIndex) {
              isValid = false;
            } else if (startScene.orderIndex === endScene.orderIndex) {
              if (startPanel.sceneOrderIndex > endPanel.sceneOrderIndex) {
                isValid = false;
              }
            }
          }

          if (!isValid) {
            message.error('End Panel must be after or equal to Start Panel in hierarchical order.');
            return;
          }
        }
      }
    }

    const action = episode 
      ? () => sourceEpisodesApi.updateEpisode(episode.id, values)
      : () => sourceEpisodesApi.createEpisode(storyId, values);

    const result = await executeAsync(action, {
      successMessage: `Episode ${episode ? 'updated' : 'created'} successfully!`,
    });

    if (result) {
      onSuccess();
    }
  };

  return (
    <Modal
      title={episode ? 'Edit Episode' : 'Create New Episode'}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={asyncLoading}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={18}>
            <Form.Item
              name="name"
              label="Episode Name"
              rules={[{ required: true, message: 'Please enter episode name' }]}
            >
              <Input placeholder="e.g. Episode 1" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="orderIndex"
              label="Order Index"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="startPanelId"
              label="Start Panel"
              rules={[{ required: true, message: 'Select start panel' }]}
            >
              <HierarchicalPanelPicker
                processes={allProcesses}
                scenes={allScenes}
                panels={allPanels}
                loading={isLoading}
                placeholder="Select start panel"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="endPanelId"
              label="End Panel"
              rules={[{ required: true, message: 'Select end panel' }]}
            >
              <HierarchicalPanelPicker
                processes={allProcesses}
                scenes={allScenes}
                panels={allPanels}
                loading={isLoading}
                placeholder="Select end panel"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Status"
            >
              <Select>
                <Option value="DRAFT">Draft</Option>
                <Option value="PUBLISHED">Published</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="thumbnailUrl"
              label="Thumbnail URL (Optional)"
            >
              <Input placeholder="Direct URL to image" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditEpisodeModal;
